import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component, DestroyRef, inject,
    Input,
    OnInit
} from "@angular/core";
import { IRowState } from "../../interfaces/row-state";
import { RowStateType } from "../../enums/row-state-type";

import { MatIcon } from "@angular/material/icon";
import { MatTooltip } from "@angular/material/tooltip";
import { MatHeaderCell } from "@angular/material/table";
import { LIST_STATE_SERVICE } from "../../constants/tokens";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";

/**
 * Displays an error or warning with message for a specific row based on its row state
 *
 * Should be added to the info column of your matTable:
 *
 * <ng-container cdkColumnDef="info">
 *           <mat-header-cell *cdkHeaderCellDef>
 *               <mgd-row-state-info></mgd-row-state-info>
 *           </mat-header-cell>
 *            <mat-cell *cdkCellDef="let row">
 *               <mgd-row-state-info></mgd-row-state-info>
 *           </mat-cell>
 * </ng-container>
 *
 */
@Component({
    selector: "mgd-row-state-info",
    templateUrl: "./row-state-info.component.html",
    styleUrls: ["./row-state-info.component.scss"],
    imports: [MatIcon, MatTooltip],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class RowStateInfoComponent implements OnInit {

    /**
     * The row stat to display
     */
    @Input() rowState?: IRowState;

    @Input() row?: object;

    /**
     * Expose the enum to the template
     */
    rowStateType = RowStateType;

    /**
     * Will be true if the parent is the header row
     * Used to setup the subscriptions
     */
    private readonly _headerRow? = inject(MatHeaderCell, {
        host: true,
        optional: true
    });

    /**
     * The list state service for the given list
     *
     */
    private readonly _listState? = inject(LIST_STATE_SERVICE, {
        optional: true
    });

    /**
     * Used to end subscriptions when component is destroyed
     */
    private readonly _destroyRef = inject(DestroyRef);

    private readonly _changeDetector = inject(ChangeDetectorRef);

    ngOnInit(): void {
        if (this._headerRow && this._listState) {
            this._listState.infoHeaderState$
                .pipe(
                    takeUntilDestroyed(this._destroyRef)
                )
                .subscribe((state) => this._setState(state));
        }
        else if (this.row && this._listState) {
            this._listState.getRowState$(this.row)
                .pipe(
                    takeUntilDestroyed(this._destroyRef)
                )
                .subscribe((state) => this._setState(state));
        }
    }

    private _setState(state: IRowState): void {
        this.rowState = state;
        this._changeDetector.markForCheck();
    };
}
